import { queryKeys } from '@/shared/api';
import type { ServerEvent } from '@/shared/realtime';

/** While the socket is not open, active queries are re-read this often (visible tabs only). */
export const FALLBACK_INTERVAL_MS = 30_000;
/** A tab hidden longer than this is refreshed when it comes back, whatever the socket says. */
export const STALE_AFTER_HIDDEN_MS = 30_000;

type Key = readonly unknown[];

/** The global calendar (every board the viewer sees): a board appearing or vanishing changes it. */
const GLOBAL_CALENDAR_KEY: Key = [...queryKeys.calendar.all, { kind: 'all' }];

/** The personal planner: its list / summary and its calendar (scope `me`), nothing else. */
const PLANNER_KEYS: readonly Key[] = [
  queryKeys.myTasks.summary,
  queryKeys.myTasks.listAll,
  [...queryKeys.calendar.all, { kind: 'me' }],
];

/** What a catalog of databases shows: the viewer's whole list and every workspace's list. */
const DATABASE_CATALOG_KEYS: readonly Key[] = [queryKeys.databases.mine, ['databases', 'list']];

/**
 * Every committed change adds an entry to a database's History: an OPEN History (database or
 * record) is read again - queries that are not on screen are only marked stale.
 */
const DATABASE_HISTORY_KEYS: readonly Key[] = [
  ['databases', 'history'],
  ['databases', 'record-history'],
];

/**
 * Who may do what changed (a role, a denial, a rule, a power, the owner, a membership): what the
 * database pages show about it is read again - the detail (permissions, `content_visible`), the
 * access panel, the table list and the catalogs. Record pages, views and table details are not
 * listed: a lost database fails its detail and the page stops showing them; for a changed role
 * the data stays the same.
 */
export const DATABASE_ACCESS_KEYS: readonly Key[] = [
  ['databases', 'detail'],
  ['databases', 'access'],
  ['databases', 'tables'],
  ...DATABASE_HISTORY_KEYS,
  ...DATABASE_CATALOG_KEYS,
];

/**
 * The queries a database hint touches (ids only; whatever is refetched is authorized again).
 * `records`: the record pages of that table. `columns`: the table (columns, options, formats),
 * its records and its views (a view may name a column). `tables`: the table list and that table.
 * `views`: the SHARED (or own) views of that table. `database`: its detail and the catalogs.
 * `deleted`: the same, so the page learns at once that it is gone.
 */
export function keysForDatabase(event: ServerEvent): Key[] {
  const databaseId = typeof event.database_id === 'string' ? event.database_id : null;
  const tableId = typeof event.table_id === 'string' ? event.table_id : null;
  const keys: Key[] = [];
  switch (event.scope) {
    case 'records':
      keys.push(tableId ? queryKeys.databases.recordsAll(tableId) : ['databases', 'records']);
      break;
    case 'columns':
      if (tableId) {
        keys.push(
          queryKeys.databases.table(tableId),
          queryKeys.databases.recordsAll(tableId),
          queryKeys.databases.views(tableId),
        );
      } else {
        keys.push(['databases', 'table'], ['databases', 'records'], ['databases', 'views']);
      }
      break;
    case 'tables':
      if (databaseId) keys.push(queryKeys.databases.tables(databaseId));
      if (tableId) keys.push(queryKeys.databases.table(tableId));
      break;
    case 'views':
      keys.push(tableId ? queryKeys.databases.views(tableId) : ['databases', 'views']);
      break;
    case 'database':
    case 'deleted':
      if (databaseId) keys.push(queryKeys.databases.detail(databaseId));
      keys.push(...DATABASE_CATALOG_KEYS);
      if (event.scope === 'deleted' && databaseId)
        keys.push(queryKeys.databases.tables(databaseId));
      break;
    default:
  }
  // an entry was added to the History as well (only for a scope this client understands)
  return keys.length > 0 ? [...keys, ...DATABASE_HISTORY_KEYS] : keys;
}

/** Record pages change often: they are refreshed at most this often per table (a burst of
 * edits by others costs one read, not one per edit). Everything else uses the short window. */
export const RECORDS_MIN_INTERVAL_MS = 1000;

/**
 * The query scopes a collection hint touches. Narrow on purpose: a hint about the board list
 * must not reload every board, and one about members must not reload the boards at all. The
 * hints carry ids only; whatever is refetched is authorized again by the server.
 */
export function keysForCollection(event: ServerEvent): Key[] {
  const workspaceId = typeof event.workspace_id === 'string' ? event.workspace_id : null;
  const boardId = event.board_id ?? null;
  const keys: Key[] = [];
  switch (event.scope) {
    case 'tasks':
      // A task of the planner changed (deadline, state, assignment, place): only the planner.
      keys.push(...PLANNER_KEYS);
      break;
    case 'boards':
      // A board appeared, was renamed, trashed or lost: its tasks may have gone with it.
      keys.push(queryKeys.collab.myBoards, ...PLANNER_KEYS, GLOBAL_CALENDAR_KEY);
      if (workspaceId) keys.push(queryKeys.boards.list(workspaceId));
      if (boardId) keys.push(queryKeys.boards.detail(boardId));
      break;
    case 'databases':
      // A database appeared, was renamed, restyled, trashed, moved or changed hands: catalogs only.
      keys.push(...DATABASE_CATALOG_KEYS);
      if (workspaceId) keys.push(queryKeys.databases.list(workspaceId));
      break;
    case 'sections':
      // The section tree changed (created, renamed, moved, deleted): the list only.
      if (workspaceId) keys.push(queryKeys.sections.list(workspaceId));
      break;
    case 'groups':
      if (workspaceId) keys.push(queryKeys.groups.list(workspaceId));
      break;
    case 'documents':
      // A document appeared, was renamed, moved, trashed, or who may read it changed.
      keys.push(queryKeys.documents.mine, ['documents', 'detail']);
      if (workspaceId) {
        keys.push(queryKeys.documents.list(workspaceId), queryKeys.documents.trash(workspaceId));
      }
      break;
    case 'members':
      if (workspaceId) keys.push(queryKeys.collab.members(workspaceId), queryKeys.workspaces.all);
      break;
    case 'teams':
      if (workspaceId) keys.push(queryKeys.collab.teams(workspaceId));
      if (workspaceId) keys.push(queryKeys.collab.members(workspaceId));
      break;
    case 'roles':
      if (workspaceId) {
        keys.push(
          queryKeys.collab.roles(workspaceId),
          queryKeys.collab.matrix(workspaceId),
          queryKeys.collab.hierarchy(workspaceId),
          queryKeys.collab.members(workspaceId),
        );
        keys.push(queryKeys.workspaces.all);
      }
      break;
    case 'labels':
      if (workspaceId) keys.push(queryKeys.collab.boardLabels(workspaceId));
      keys.push(queryKeys.collab.myBoards);
      break;
    case 'rules':
      if (workspaceId) keys.push(queryKeys.collab.rules(workspaceId));
      break;
    case 'invitations':
      if (workspaceId) {
        keys.push(queryKeys.collab.invitations('workspace', workspaceId));
        keys.push(queryKeys.collab.links('workspace', workspaceId));
      }
      keys.push(queryKeys.collab.myInvitations);
      break;
    case 'requests':
      if (workspaceId) keys.push(queryKeys.collab.requests(workspaceId));
      keys.push(queryKeys.collab.myRequests);
      break;
    case 'audit':
      if (workspaceId) keys.push(queryKeys.collab.audit(workspaceId));
      break;
    case 'workspace':
      keys.push(queryKeys.workspaces.all);
      if (workspaceId) keys.push(queryKeys.workspaces.detail(workspaceId));
      break;
    default:
  }
  return keys;
}

/** Whether returning to the tab should refetch what is on screen. */
export function shouldRefreshOnVisible(
  hiddenForMs: number,
  state: 'idle' | 'connecting' | 'open' | 'closed',
): boolean {
  return state !== 'open' || hiddenForMs >= STALE_AFTER_HIDDEN_MS;
}
